/**
 * XO Club - Capacitor Native Integration
 * =======================================
 * Hook pour gérer les fonctionnalités natives iOS/Android
 */

import { useEffect, useCallback, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Network } from '@capacitor/network';
import { SplashScreen } from '@capacitor/splash-screen';
import { PushNotifications } from '@capacitor/push-notifications';
import { Preferences } from '@capacitor/preferences';
import { useNavigate } from 'react-router-dom';
import logger from '../utils/logger';

// Vérifier si on est en mode natif (iOS/Android)
export const isNative = Capacitor.isNativePlatform();
export const isIOS = Capacitor.getPlatform() === 'ios';
export const isAndroid = Capacitor.getPlatform() === 'android';
export const isWeb = Capacitor.getPlatform() === 'web';

/**
 * Hook principal pour initialiser Capacitor
 */
export const useCapacitor = () => {
  const navigate = useNavigate();
  const [isOnline, setIsOnline] = useState(true);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  // Initialisation au montage
  useEffect(() => {
    // Log platform info for debugging
    if (Capacitor.isNativePlatform()) {
      logger.log('[XO Club] Running as native app on:', Capacitor.getPlatform());
    } else {
      logger.log('[XO Club] Running as web app');
    }
    
    if (!isNative) return;

    const init = async () => {
      try {
        // Cacher le splash screen après chargement
        await SplashScreen.hide();

        // Configurer la status bar
        await StatusBar.setStyle({ style: Style.Dark });
        if (isAndroid) {
          await StatusBar.setBackgroundColor({ color: '#0a1628' });
        }
      } catch (e) {
        logger.debug('Capacitor init:', e);
      }
    };

    init();
  }, []);

  // Gestion du bouton back Android
  useEffect(() => {
    if (!isNative) return;

    const backHandler = CapApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        CapApp.exitApp();
      }
    });

    return () => {
      backHandler.remove();
    };
  }, []);

  // Gestion Deep Links
  useEffect(() => {
    if (!isNative) return;

    const urlHandler = CapApp.addListener('appUrlOpen', (event) => {
      try {
        const url = new URL(event.url);
        const path = url.pathname;
        if (path) {
          navigate(path);
        }
      } catch (e) {
        logger.debug('Deep link error:', e);
      }
    });

    return () => {
      urlHandler.remove();
    };
  }, [navigate]);

  // Gestion réseau
  useEffect(() => {
    if (!isNative) return;

    const checkNetwork = async () => {
      const status = await Network.getStatus();
      setIsOnline(status.connected);
    };

    checkNetwork();

    const networkHandler = Network.addListener('networkStatusChange', (status) => {
      setIsOnline(status.connected);
    });

    return () => {
      networkHandler.remove();
    };
  }, []);

  // Gestion clavier
  useEffect(() => {
    if (!isNative) return;

    const showHandler = Keyboard.addListener('keyboardWillShow', (info) => {
      setKeyboardVisible(true);
      setKeyboardHeight(info.keyboardHeight);
    });

    const hideHandler = Keyboard.addListener('keyboardWillHide', () => {
      setKeyboardVisible(false);
      setKeyboardHeight(0);
    });

    return () => {
      showHandler.remove();
      hideHandler.remove();
    };
  }, []);

  return {
    isNative,
    isIOS,
    isAndroid,
    isOnline,
    keyboardVisible,
    keyboardHeight
  };
};

/**
 * Hook pour les Push Notifications natives (FCM)
 */
export const usePushNotifications = (userId) => {
  const [fcmToken, setFcmToken] = useState(null);
  const [permissionGranted, setPermissionGranted] = useState(false);

  const registerPush = useCallback(async () => {
    if (!isNative) return null;

    try {
      const permission = await PushNotifications.requestPermissions();
      
      if (permission.receive === 'granted') {
        setPermissionGranted(true);
        await PushNotifications.register();
        return true;
      }
      
      return false;
    } catch (error) {
      logger.error('Push registration error:', error);
      return false;
    }
  }, []);

  useEffect(() => {
    if (!isNative) return;

    const tokenHandler = PushNotifications.addListener('registration', (token) => {
      logger.log('FCM Token:', token.value);
      setFcmToken(token.value);
      
      if (userId && token.value) {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/push/register-fcm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            userId,
            fcmToken: token.value,
            platform: isIOS ? 'ios' : 'android'
          })
        }).catch(e => logger.error('FCM registration failed:', e));
      }
    });

    const errorHandler = PushNotifications.addListener('registrationError', (error) => {
      logger.error('Push registration error:', error);
    });

    const notificationHandler = PushNotifications.addListener(
      'pushNotificationReceived',
      (notification) => {
        logger.debug('Push received:', notification);
      }
    );

    const actionHandler = PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action) => {
        logger.debug('Push action:', action);
        const data = action.notification.data;
        if (data?.url) {
          window.location.href = data.url;
        }
      }
    );

    return () => {
      tokenHandler.remove();
      errorHandler.remove();
      notificationHandler.remove();
      actionHandler.remove();
    };
  }, [userId]);

  return {
    fcmToken,
    permissionGranted,
    registerPush
  };
};

/**
 * Haptic Feedback
 */
export const haptics = {
  light: () => isNative && Haptics.impact({ style: ImpactStyle.Light }),
  medium: () => isNative && Haptics.impact({ style: ImpactStyle.Medium }),
  heavy: () => isNative && Haptics.impact({ style: ImpactStyle.Heavy }),
  success: () => isNative && Haptics.notification({ type: NotificationType.Success }),
  warning: () => isNative && Haptics.notification({ type: NotificationType.Warning }),
  error: () => isNative && Haptics.notification({ type: NotificationType.Error }),
  selection: () => isNative && Haptics.selectionStart()
};

/**
 * Storage natif sécurisé (remplace localStorage sur iOS)
 */
export const nativeStorage = {
  get: async (key) => {
    if (isNative) {
      const { value } = await Preferences.get({ key });
      return value;
    }
    return localStorage.getItem(key);
  },
  
  set: async (key, value) => {
    if (isNative) {
      await Preferences.set({ key, value });
    } else {
      localStorage.setItem(key, value);
    }
  },
  
  remove: async (key) => {
    if (isNative) {
      await Preferences.remove({ key });
    } else {
      localStorage.removeItem(key);
    }
  },
  
  clear: async () => {
    if (isNative) {
      await Preferences.clear();
    } else {
      localStorage.clear();
    }
  }
};

/**
 * Safe Area - Gère le notch iPhone et la barre Android
 */
export const useSafeArea = () => {
  const [safeArea, setSafeArea] = useState({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  });

  useEffect(() => {
    if (typeof window !== 'undefined' && isNative) {
      // Les valeurs CSS env() pour safe area
      const root = document.documentElement;
      root.style.setProperty('--sat', 'env(safe-area-inset-top)');
      root.style.setProperty('--sab', 'env(safe-area-inset-bottom)');
      root.style.setProperty('--sal', 'env(safe-area-inset-left)');
      root.style.setProperty('--sar', 'env(safe-area-inset-right)');
    }
  }, []);

  return safeArea;
};

export default useCapacitor;
